import React, { useRef, useState } from "react";
import { Alert, Box, Button, Link, List, ListItem, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import {
  capacityTemplateUrl,
  uploadCapacitySchedule,
} from "../requests/location-requests";
import { fetchDownloadFile } from "../../../util/FileUtils";
import type { CapacityUploadResult } from "../../../types/LocationCapacitySchedule";
import { getErrorMessage } from "../../../util/NetworkErrorUtil";

interface CapacityBulkUploadProps {
  locationId: string;
  /** Called after a successful upload so the parent can refresh the schedule. */
  onUploaded: (result: CapacityUploadResult) => void;
}

/**
 * POC option (d): bulk capacity upload from a CSV / Excel-exported CSV file.
 * Suppliers maintaining many locations can paste from a spreadsheet instead of
 * hand-editing rows. Downloads a template and reports per-row errors.
 */
const CapacityBulkUpload = ({ locationId, onUploaded }: CapacityBulkUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<CapacityUploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setFileName(file.name);
    setUploading(true);
    setError(null);
    setResult(null);
    try {
      const response = await uploadCapacitySchedule(locationId, file);
      const body = response.body as CapacityUploadResult;
      setResult(body);
      onUploaded(body);
    } catch (err) {
      setError(getErrorMessage(err) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownloadTemplate = () => {
    fetchDownloadFile(
      capacityTemplateUrl(locationId),
      undefined,
      (err) => setError(getErrorMessage(err) || "Could not download the template"),
    );
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "#5E7782", mb: 2 }}>
        Upload a spreadsheet of capacity changes. Save your Excel or Google Sheet
        as CSV with the columns <strong>effective_date, end_date, capacity, note</strong>.
        Uploading replaces the current schedule for this location.
      </Typography>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
          }}
        />
        <Button
          variant="contained"
          startIcon={<UploadFileIcon />}
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? "Uploading…" : "Choose CSV file"}
        </Button>
        <Link
          component="button"
          type="button"
          onClick={handleDownloadTemplate}
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.85rem" }}
        >
          <DownloadIcon fontSize="small" /> Download template
        </Link>
        {fileName && (
          <Typography variant="caption" sx={{ color: "#8299A5" }}>
            {fileName}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Alert
          severity={result.errors.length > 0 ? "warning" : "success"}
          sx={{ mt: 2 }}
        >
          Processed {result.rowsProcessed} row(s), accepted {result.rowsAccepted}.
          {result.errors.length > 0 && (
            <List dense sx={{ mt: 0.5, listStyleType: "disc", pl: 3 }}>
              {result.errors.map((message, i) => (
                <ListItem key={i} sx={{ display: "list-item", p: 0 }}>
                  <Typography variant="caption">{message}</Typography>
                </ListItem>
              ))}
            </List>
          )}
        </Alert>
      )}
    </Box>
  );
};

export default CapacityBulkUpload;
